/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Enums, ExtensionManager, MODULE_TYPES, log } from '@ohif/core';
//
import { extensionManager } from '../App';
import { useParams, useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import useSearchParams from '../hooks/useSearchParams';
import {
  getDicomStorePathFromConfig,
  getDicomStorePathFromSearch,
} from '../utils/dicomStoreUrl';
import { applyDicomStoreToSession } from '../utils/resolveStudyDicomStore';
import { redirectToNotFoundStudy } from '../utils/redirectToNotFoundStudy';
import { isPermissionDeniedError } from '../utils/studyNotFound';

function _normalizeStorePath(storePath?: string | null): string {
  return String(storePath || '')
    .trim()
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .toLowerCase();
}

function _resolveActiveDataSourceName(getDataSourceName: () => string): string {
  return (
    getDataSourceName() ||
    extensionManager.activeDataSourceName ||
    window.config?.defaultDataSourceName ||
    'dicomweb'
  );
}

/**
 * If the URL names a DICOM store, make sure the active data source uses it.
 * Returns true when configuration was changed.
 */
function ensureDicomStoreFromUrl(search: string, getDataSourceName: () => string): boolean {
  const storePath = getDicomStorePathFromSearch(search);
  if (!storePath) {
    return false;
  }

  const dataSourceName = _resolveActiveDataSourceName(getDataSourceName);
  const dataSourceDef = extensionManager.getDataSourceDefinition(dataSourceName);
  if (!dataSourceDef) {
    return false;
  }

  const currentStorePath = getDicomStorePathFromConfig(dataSourceDef.configuration);
  if (_normalizeStorePath(currentStorePath) === _normalizeStorePath(storePath)) {
    return false;
  }

  applyDicomStoreToSession({
    extensionManager,
    dataSourceName,
    storePath,
    persistDicomStoreSelection:
      dataSourceDef.configuration?.persistDicomStoreSelection !== false,
  });

  return true;
}

/**
 * Determines if two React Router location objects are the same.
 */
const areLocationsTheSame = (location0, location1) => {
  return (
    location0.pathname === location1.pathname &&
    location0.search === location1.search &&
    location0.hash === location1.hash
  );
};

/**
 * Uses route properties to determine the data source that should be passed
 * to the child layout template. In some instances, initiates requests and
 * passes data as props.
 *
 * @param {object} props
 * @param {function} props.children - Layout Template React Component
 */
function DataSourceWrapper(props: withAppTypes) {
  const { servicesManager } = props;
  const navigate = useNavigate();
  const { children: LayoutTemplate, ...rest } = props;
  const params = useParams();
  const location = useLocation();
  const lowerCaseSearchParams = useSearchParams({ lowerCaseKeys: true });
  const query = useSearchParams();
  // Route props --> studies.mapParams
  // mapParams --> studies.search
  // studies.search --> studies.processResults
  // studies.processResults --> <LayoutTemplate studies={} />
  // But only for LayoutTemplate type of 'list'?
  // Or no data fetching here, and just hand down my source
  const STUDIES_LIMIT = 101;
  const DEFAULT_DATA = {
    studies: [],
    total: 0,
    resultsPerPage: 25,
    pageNumber: 1,
    location: 'Not a valid location, causes first load to occur',
  };

  const getInitialDataSourceName = useCallback(() => {
    // TODO - get the variable from the props all the time...
    let dataSourceName = lowerCaseSearchParams.get('datasources');

    if (!dataSourceName && window.config.defaultDataSourceName) {
      return '';
    }

    if (!dataSourceName) {
      // Gets the first defined datasource with the right name
      // Mostly for historical reasons - new configs should use the defaultDataSourceName
      const dataSourceModules = extensionManager.modules[MODULE_TYPES.DATA_SOURCE];
      // TODO: Good usecase for flatmap?
      const webApiDataSources = dataSourceModules.reduce((acc, curr) => {
        const mods = [];
        curr.module.forEach(mod => {
          if (mod.type === 'webApi') {
            mods.push(mod);
          }
        });
        return acc.concat(mods);
      }, []);
      dataSourceName = webApiDataSources
        .map(ds => ds.name)
        .find(it => extensionManager.getDataSources(it)?.[0] !== undefined);
    }

    return dataSourceName;
  }, []);

  const [isDataSourceInitialized, setIsDataSourceInitialized] = useState(false);

  // The path to the data source to be used in the URL for a mode (e.g. mode/dataSourcePath?StudyIntanceUIDs=1.2.3)
  const [dataSourcePath, setDataSourcePath] = useState(() => {
    const dataSourceName = getInitialDataSourceName();
    return dataSourceName ? `/${dataSourceName}` : '';
  });

  const [dataSource, setDataSource] = useState(() => {
    // New-tab / pasted links: apply dicomStore from the URL before the first query.
    ensureDicomStoreFromUrl(
      typeof window !== 'undefined' ? window.location.search : '',
      getInitialDataSourceName
    );

    const dataSourceName = getInitialDataSourceName();

    if (!dataSourceName) {
      return extensionManager.getActiveDataSource()[0];
    }

    const resolved = extensionManager.getDataSources(dataSourceName)?.[0];
    if (!resolved) {
      throw new Error(`No data source found for ${dataSourceName}`);
    }

    return resolved;
  });

  const [data, setData] = useState(DEFAULT_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [storeAccessDenied, setStoreAccessDenied] = useState(false);
  const searchRequestIdRef = useRef(0);
  const permissionRedirectStartedRef = useRef(false);

  const handleStudyListLoadError = useCallback(
    (error: unknown) => {
      if (isPermissionDeniedError(error)) {
        if (permissionRedirectStartedRef.current) {
          return;
        }
        permissionRedirectStartedRef.current = true;
        setStoreAccessDenied(true);
        // Full-page error (same pattern as viewer study links) — do not keep Study List open.
        redirectToNotFoundStudy('permission-denied');
        return;
      }

      const { configurationAPI, friendlyName } = dataSource.getConfig?.() || {};
      // Non-permission failures: keep legacy behavior (config modal handles incomplete setup).
      if (configurationAPI) {
        return;
      }

      servicesManager.services.uiModalService?.show?.({
        title: 'Data Source Connection Error',
        content: () => (
          <div className="text-foreground">
            <p className="text-red-600">Error: {(error as Error)?.message || String(error)}</p>
            <p>Please ensure the following data source is configured correctly or is running:</p>
            <div className="mt-2 font-bold">{friendlyName}</div>
          </div>
        ),
      });
    },
    [dataSource, servicesManager.services]
  );

  // Subscribe first so store switches from ensureDicomStoreFromUrl are never missed.
  useEffect(() => {
    const dataSourceChangedCallback = () => {
      searchRequestIdRef.current += 1;
      permissionRedirectStartedRef.current = false;
      setStoreAccessDenied(false);
      setIsLoading(false);
      setIsDataSourceInitialized(false);
      setDataSourcePath('');
      setDataSource(extensionManager.getActiveDataSource()[0]);
      // Setting data to DEFAULT_DATA triggers a new query just like it does for the initial load.
      setData(DEFAULT_DATA);
    };

    const sub = extensionManager.subscribe(
      ExtensionManager.EVENTS.ACTIVE_DATA_SOURCE_CHANGED,
      dataSourceChangedCallback
    );
    return () => sub.unsubscribe();
  }, []);

  // Keep the active store in sync when the address bar dicomStore param changes
  // (e.g. OIDC callback navigates back with the original shareable link).
  // Use window.location.search — store selection updates the bar via replaceState,
  // which does not update React Router's location.search.
  useEffect(() => {
    const browserSearch =
      typeof window !== 'undefined' ? window.location.search : location.search;
    const switched = ensureDicomStoreFromUrl(browserSearch, getInitialDataSourceName);
    const active = extensionManager.getActiveDataSource()?.[0];

    if (switched) {
      searchRequestIdRef.current += 1;
      permissionRedirectStartedRef.current = false;
      setStoreAccessDenied(false);
      setIsLoading(false);
      setIsDataSourceInitialized(false);
      setDataSource(active);
      setData(DEFAULT_DATA);
      return;
    }

    if (active && active !== dataSource) {
      searchRequestIdRef.current += 1;
      permissionRedirectStartedRef.current = false;
      setStoreAccessDenied(false);
      setIsLoading(false);
      setIsDataSourceInitialized(false);
      setDataSource(active);
      setData(DEFAULT_DATA);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, getInitialDataSourceName]);

  /**
   * The effect to initialize the data source whenever it changes. Similar to
   * whenever a different Mode is entered, the Mode's data source is initialized, so
   * too this DataSourceWrapper must initialize its data source whenever a different
   * data source is activated. Furthermore, a data source might be initialized
   * several times as it gets activated/deactivated because the location URL
   * might change and data sources initialize based on the URL.
   */
  useEffect(() => {
    let cancelled = false;

    const initializeDataSource = async () => {
      await dataSource.initialize({ params, query });
      if (!cancelled) {
        setIsDataSourceInitialized(true);
      }
    };

    setIsDataSourceInitialized(false);
    initializeDataSource();

    return () => {
      cancelled = true;
    };
  }, [dataSource]);

  useEffect(() => {
    if (!isDataSourceInitialized || storeAccessDenied) {
      return;
    }

    const queryFilterValues = _getQueryFilterValues(location.search, STUDIES_LIMIT);

    // 204: no content
    async function getData() {
      const requestId = ++searchRequestIdRef.current;
      setIsLoading(true);
      log.time(Enums.TimingEnum.SEARCH_TO_LIST);
      try {
        const studies = await dataSource.query.studies.search(queryFilterValues);

        if (requestId !== searchRequestIdRef.current) {
          return;
        }

        setData({
          studies: studies || [],
          total: studies.length,
          resultsPerPage: queryFilterValues.resultsPerPage,
          pageNumber: queryFilterValues.pageNumber,
          location,
        });
        log.timeEnd(Enums.TimingEnum.SCRIPT_TO_VIEW);
        log.timeEnd(Enums.TimingEnum.SEARCH_TO_LIST);
      } catch (e) {
        if (requestId !== searchRequestIdRef.current) {
          return;
        }

        // Mark this location as resolved so the effect does not retry forever on 403.
        setData({
          studies: [],
          total: 0,
          resultsPerPage: queryFilterValues.resultsPerPage,
          pageNumber: queryFilterValues.pageNumber,
          location,
        });
        throw e;
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    }

    try {
      // Cache invalidation :thinking:
      // - Anytime change is not just next/previous page
      // - And we didn't cross a result offset range
      const isSamePage = data.pageNumber === queryFilterValues.pageNumber;
      const previousOffset =
        Math.floor((data.pageNumber * data.resultsPerPage) / STUDIES_LIMIT) * (STUDIES_LIMIT - 1);
      const newOffset =
        Math.floor(
          (queryFilterValues.pageNumber * queryFilterValues.resultsPerPage) / STUDIES_LIMIT
        ) *
        (STUDIES_LIMIT - 1);
      // Simply checking data.location !== location is not sufficient because even though the location href (i.e. entire URL)
      // has not changed, the React Router still provides a new location reference and would result in two study queries
      // on initial load. Alternatively, window.location.href could be used.
      const isLocationUpdated =
        typeof data.location === 'string' || !areLocationsTheSame(data.location, location);
      const isDataInvalid =
        !isSamePage || (!isLoading && (newOffset !== previousOffset || isLocationUpdated));

      if (isDataInvalid) {
        getData().catch(e => {
          console.error(e);
          handleStudyListLoadError(e);
        });
      }
    } catch (ex) {
      console.warn(ex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    location,
    params,
    isLoading,
    setIsLoading,
    dataSource,
    isDataSourceInitialized,
    storeAccessDenied,
  ]);
  // queryFilterValues

  // Do not render Study List while redirecting to the full-page permission error.
  if (storeAccessDenied) {
    return null;
  }

  // TODO: Better way to pass DataSource?
  return (
    <LayoutTemplate
      {...rest}
      data={data.studies}
      dataPath={dataSourcePath}
      dataTotal={data.total}
      dataSource={dataSource}
      isLoadingData={isLoading}
      // To refresh the data, simply reset it to DEFAULT_DATA which invalidates it and triggers a new query to fetch the data.
      onRefresh={() => setData(DEFAULT_DATA)}
    />
  );
}

DataSourceWrapper.propTypes = {
  /** Layout Component to wrap with a Data Source */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
};

export default DataSourceWrapper;

/**
 * Duplicated in `workList`
 * Need generic that can be shared? Isn't this what qs is for?
 * @param {*} query
 */
function _getQueryFilterValues(query, queryLimit) {
  query = new URLSearchParams(query);
  const newParams = new URLSearchParams();
  for (const [key, value] of query) {
    newParams.set(key.toLowerCase(), value);
  }
  query = newParams;

  const pageNumber = _tryParseInt(query.get('pagenumber'), 1);
  const resultsPerPage = _tryParseInt(query.get('resultsperpage'), 25);

  const queryFilterValues = {
    // DCM
    patientId: query.get('mrn'),
    patientName: query.get('patientname'),
    studyDescription: query.get('description'),
    modalitiesInStudy: query.get('modalities') && query.get('modalities').split(','),
    studyInstanceUid: query.get('studyinstanceuid'),
    //
    startDate: query.get('startdate'),
    endDate: query.get('enddate'),
    page: _tryParseInt(query.get('page'), undefined),
    pageNumber,
    resultsPerPage,
    // Rarely supported server-side
    sortBy: query.get('sortby'),
    sortDirection: query.get('sortdirection'),
    // Offset...
    offset: Math.floor((pageNumber * resultsPerPage) / queryLimit) * (queryLimit - 1),
    config: query.get('configurl'),
  };

  // patientName: good
  // studyDescription: good
  // studyInstanceUid: good

  // Delete null/undefined keys
  Object.keys(queryFilterValues).forEach(
    key => queryFilterValues[key] == null && delete queryFilterValues[key]
  );

  return queryFilterValues;

  function _tryParseInt(str, defaultValue) {
    let retValue = defaultValue;
    if (str !== null) {
      if (str.length > 0) {
        if (!isNaN(str)) {
          retValue = parseInt(str);
        }
      }
    }
    return retValue;
  }
}
